import React, {useEffect, useRef, useState} from 'react'
import {Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {IApplicationFormData} from '../../models/application-form'
import {TextInput} from '../../../common/component/TextInput'
import {CheckboxInput} from '../../../common/component/CheckboxInput'
import {MobileNumberInput} from '../../../common/component/MobileNumberInput'
import {TermsModal} from '../terms/TermsModal'
import {PrivacyModal} from '../terms/PrivacyModal'
import {checkDraftExistsByMobile} from '../../api'
import { isValidAustralianMobile, normalizeMobile } from '../renter-home/renterHomeUtils'
import { getFirstName, isCoApplicantApplication, IsInviteApplication } from '../wizard/coApplicantInviteContext'

// Start props
interface StartProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData> // add errors
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
}

const Start: React.FC<StartProps> = ({register, control, errors, watch, setValue}) => {
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [draftExists, setDraftExists] = useState(false)
  const [checkingDraft, setCheckingDraft] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const selectedAction = watch('action')
  const mobileNumber = watch('mobileNumber')
  const renterRole = watch('renterRole')
  const isCoApplicant = isCoApplicantApplication({renterRole})
  const isInvited = IsInviteApplication({renterRole})
  const primaryApplicantName = watch('primaryApplicantName')
  const primaryFirstName = getFirstName(primaryApplicantName) || 'the primary renter'
  const selectedActionRef = useRef<IApplicationFormData['action']>(selectedAction)

  useEffect(() => {
    selectedActionRef.current = selectedAction
  }, [selectedAction])

  const handleActionClick = (action: 'resume' | 'new') => {
    selectedActionRef.current = action
    setValue('action', action)
  }

  // Use effect to check draft whenever mobile number changes
  useEffect(() => {
      if (isInvited) {
      setDraftExists(false)
      setCheckingDraft(false)
      selectedActionRef.current = undefined
      setValue('action', undefined)
      setValue('existingApplicationId', undefined)
      setLookupError(null)
      return
    }
    
    if (!isValidAustralianMobile(mobileNumber)) {
      setDraftExists(false)
      setCheckingDraft(false)
      selectedActionRef.current = undefined
      setValue('action', undefined)
      setValue('existingApplicationId', undefined)
      setLookupError(null)
      return
    }

    let cancelled = false
    const delayCheck = setTimeout(async () => {
      setCheckingDraft(true)
      setLookupError(null)

      try {
        const result = await checkDraftExistsByMobile(normalizeMobile(mobileNumber ?? ''))
        if (cancelled) {
          return
        }

        setDraftExists(result.exists)
        setValue('existingApplicationId', result.existingApplicationId)

        if (result.exists) {
          const nextAction = selectedActionRef.current ?? 'resume'
          selectedActionRef.current = nextAction
          setValue('action', nextAction)
        } else {
          selectedActionRef.current = 'new'
          setValue('action', 'new')
        }
      } catch {
        if (!cancelled) {
          setDraftExists(false)
          setValue('existingApplicationId', undefined)
          setLookupError('Unable to check for an existing draft right now.')
        }
      } finally {
        if (!cancelled) {
          setCheckingDraft(false)
        }
      }
    }, 500) // debounce API check

    return () => {
      cancelled = true
      clearTimeout(delayCheck)
      }
  }, [isInvited, mobileNumber, setValue])

  return (
    <div className='wizard-card wizard-auth-card wizard-start-card bg-white rounded-4 shadow-sm m-3'>
      <div className='text-center text-dark wizard-auth-head wizard-start-head'>
        <div
          className='mx-auto wizard-auth-icon wizard-start-icon d-flex justify-content-center align-items-center'
          aria-label={isInvited ? 'Confirm details icon' : 'Account setup icon'}
        >
          {isCoApplicant ? (
            <svg
              width='30'
              height='30'
              viewBox='0 0 20 20'
              fill='none'
              stroke='#BB9AE4'
              strokeWidth='1.8'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M7 10a3 3 0 100-6 3 3 0 000 6z'></path>
              <path d='M2.5 17a4.5 4.5 0 019 0'></path>
              <path d='M14 10.5a2.3 2.3 0 100-4.6'></path>
              <path d='M13.2 13.2A4 4 0 0117.5 17'></path>
            </svg>
            ) : (
            <svg
              width='28'
              height='28'
              viewBox='0 0 20 20'
              fill='none'
              stroke='#BB9AE4'
              strokeWidth='1.8'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <rect x='2.5' y='5' width='15' height='10' rx='2'></rect>
              <path d='M2.5 9h15'></path>
            </svg>
          )}
        </div>
         <h2 className='wizard-auth-title wizard-start-title'>
          {isInvited ? "Confirm it's you" : "Let's find you a home."}
        </h2>
        <p className='wizard-auth-subtitle wizard-start-subtitle'>
         {isInvited ? (
            <>
              {primaryFirstName} sent this to the number below.
              <br />
              We&apos;ll text a code to verify.
            </>
          ) : (
            <>
              We&apos;ll text a code to verify.
              <br />
              This is your login.
            </>
          )}
        </p>
      </div>

      <div className='wizard-start-fields'>
        {isInvited && (
          <div className='wizard-start-prefill-note'>
            <i className='bi bi-check2' aria-hidden='true'></i>
            Pre-filled from {primaryFirstName}&apos;s invite.
          </div>
        )}
        <TextInput
          name='fullName'
          label='Full Name'
          placeholder={isInvited ? 'Full name' : 'e.g. Sarah Johnson'}
          register={register}
          errors={errors}
          className='wizard-start-field'
          inputClassName='wizard-start-input'
        />
        <MobileNumberInput
          name='mobileNumber'
          label='Mobile Number'
          placeholder='0412 345 678'
          control={control}
          errors={errors}
          className='wizard-start-field'
          inputClassName='wizard-start-input'
        />
      </div>

      {checkingDraft && (
        <div className='wizard-start-status text-muted small'>Checking for existing draft...</div>
      )}
      {lookupError && <div className='wizard-start-status text-danger small'>{lookupError}</div>}
      {draftExists && (
        <div className='wizard-start-draft-box'>
          <label className='form-label wizard-start-draft-label'>
            We found an existing draft with this mobile number. Choose what to do:
          </label>
          <div className='wizard-start-draft-actions'>
            <button
              type='button'
              className={`btn ${
                selectedAction === 'resume' ? 'btn-doora' : 'btn-outline-secondary'
              }`}
              onClick={() => handleActionClick('resume')}
            >
              Resume Application
            </button>
            <button
              type='button'
              className={`btn ${selectedAction === 'new' ? 'btn-doora' : 'btn-outline-secondary'}`}
              onClick={() => handleActionClick('new')}
            >
              Start New
            </button>
          </div>
          {errors?.action && (
            <div className='text-danger mt-1'>{String(errors.action?.message)}</div>
          )}
        </div>
      )}

      <CheckboxInput
        className='wizard-start-check'
        inputClassName='wizard-start-check-input terms'
        name='termsAccepted'
        label={
          <>
            I agree to the{' '}
            <span
              className='wizard-start-inline-link'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setShowTerms(true)
              }}
            >
              Terms
            </span>{' '}
            and{' '}
            <span
              className='wizard-start-inline-link'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setShowPrivacy(true)
              }}
            >
              Privacy Policy
            </span>
          </>
        }
        register={register}
        errors={errors}
      />
      <CheckboxInput
        name='consentSharingAccepted'
        className='wizard-start-check wizard-start-check-secondary ink800'
        inputClassName='wizard-start-check-input '
        label={
          isCoApplicant
            ? 'I consent to anyone on this application submitting on my behalf.'
            : 'I consent to sharing my application with agents when I choose'
        }
        register={register}
        errors={errors}
      />
      <TermsModal show={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyModal show={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  )
}

export default Start
